import { useEffect, useState } from 'react';
import { Palette, Type, PenLine, Save, RotateCcw, Loader2, Sparkles, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useBrand, LOGO_ICONS } from './BrandProvider';
import { api } from '../lib/api';
import { applyBrand, DEFAULT_BRAND, FONTS, PRESETS } from '../lib/brand';

function Section({ icon: Icon, title, children }) {
  return (
    <section className="glass glass-edge rounded-2xl p-5 sm:p-6 space-y-4 rise">
      <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-cyan-300" /><h2 className="text-sm font-bold tracking-wider text-slate-100">{title}</h2></div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return <label className="block space-y-1.5"><span className="t-label">{label}</span>{children}</label>;
}

function ColorField({ label, value, onChange, k }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 p-2.5">
      <input data-testid={`brand-color-${k}`} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 cursor-pointer rounded-lg border-0 bg-transparent" />
      <div className="min-w-0"><div className="text-xs text-slate-200">{label}</div><div className="text-[10px] text-slate-500 font-mono uppercase">{value}</div></div>
    </div>
  );
}

function FontField({ label, value, onChange, k }) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={`brand-font-${k}`} className="bg-black/30" style={{ fontFamily: `"${value}"` }}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-titan-surface border-violet-500/30 max-h-72">
          {FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
  );
}

function toPng(file) {
  return new Promise((ok, ko) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, 512 / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      ok(c.toDataURL('image/png'));
    };
    img.onerror = ko;
    img.src = URL.createObjectURL(file);
  });
}

function LogoSection() {
  const { brand, setBrand, logoUrl } = useBrand();
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(e.message); } setBusy(false); };
  const [url, setUrl] = useState('');
  const send = (body) => run(async () => { const r = await api('/admin/logo', { method: 'POST', body, auth: true }); setBrand({ ...brand, logoVersion: r.logoVersion }); setUrl(''); toast.success('Logo pubblicato'); });
  const upload = (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) toPng(f).then((image) => send({ image })); };
  const remove = () => run(async () => { await api('/admin/logo', { method: 'DELETE', auth: true }); setBrand({ ...brand, logoVersion: 0 }); });
  return (
    <Section icon={ImageIcon} title="Logo e icona">
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-2xl border border-white/10 bg-black/30 flex items-center justify-center overflow-hidden">
          {logoUrl ? <img data-testid="brand-logo-preview" src={logoUrl} alt="" className="h-full w-full object-contain" /> : <span className="text-[10px] text-slate-500">Nessuno</span>}
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 rounded-full border border-cyan-500/40 px-4 py-2 text-xs font-semibold text-cyan-200 hover:bg-cyan-500/10 cursor-pointer w-fit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Carica logo (PNG/JPG)
            <input data-testid="brand-logo-upload" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} />
          </label>
          <div className="flex gap-2"><Input data-testid="brand-logo-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="oppure URL immagine https://…" className="h-8 text-xs" /><button data-testid="brand-logo-url-btn" disabled={!/^https?:\/\//.test(url) || busy} onClick={() => send({ url })} className="rounded-lg border border-white/15 px-3 text-[11px] disabled:opacity-40">Usa</button></div>
          {logoUrl && <button data-testid="brand-logo-remove" onClick={remove} className="text-[11px] text-rose-300 hover:text-rose-200">Rimuovi logo</button>}
          <p className="text-[10px] text-slate-500 t-chat">Appare nell'intestazione e come icona della scheda del browser. Pubblicato subito.</p>
        </div>
      </div>
    </Section>
  );
}

export default function BrandEditor() {
  const { brand, setBrand, logoUrl } = useBrand();
  const [d, setD] = useState(brand);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setD((x) => ({ ...x, ...patch }));
  const strip = ({ updated_at, logoVersion, ...x }) => x; // eslint-disable-line no-unused-vars
  const dirty = JSON.stringify(strip(d)) !== JSON.stringify(strip(brand));

  useEffect(() => { applyBrand(d); }, [d]);
  useEffect(() => () => applyBrand(JSON.parse(localStorage.getItem('nexora.brand') || 'null') || DEFAULT_BRAND), []);

  const save = async () => {
    setBusy(true);
    try {
      const { updated_at, ...body } = d; // eslint-disable-line no-unused-vars
      const saved = await api('/brand', { method: 'PUT', body: { ...DEFAULT_BRAND, ...body }, auth: true });
      setBrand(saved); setD({ ...DEFAULT_BRAND, ...saved });
      toast.success('Aspetto salvato: ora lo vedono tutti i giocatori');
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="brand-editor" className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="t-label mb-3">Personalizza tutta l'app · anteprima dal vivo</div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-50">Aspetto<span className="text-cyan-400">.</span></h1>
          </div>
          <div className="flex gap-2">
            <button data-testid="brand-reset-btn" onClick={() => setD({ ...DEFAULT_BRAND })} className="flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold border border-white/15 text-slate-300 hover:bg-white/5"><RotateCcw className="h-4 w-4" />Predefinito</button>
            <button data-testid="brand-save-btn" onClick={save} disabled={busy || !dirty} className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold bg-gradient-to-r from-violet-600 to-cyan-600 disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salva e pubblica
            </button>
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <div className="space-y-6">
            <LogoSection />
            <Section icon={PenLine} title="Nome e scritte">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nome app"><Input data-testid="brand-name" value={d.name} maxLength={30} onChange={(e) => set({ name: e.target.value })} /></Field>
                <Field label="Sottotitolo"><Input data-testid="brand-tagline" value={d.tagline} maxLength={60} onChange={(e) => set({ tagline: e.target.value })} /></Field>
                <Field label="Etichetta banner"><Input data-testid="brand-hero-label" value={d.heroLabel} maxLength={30} onChange={(e) => set({ heroLabel: e.target.value })} /></Field>
                <Field label="Titolo banner Store"><Input data-testid="brand-hero-title" value={d.heroTitle} maxLength={80} onChange={(e) => set({ heroTitle: e.target.value })} /></Field>
              </div>
              <Field label="Testo banner Store"><Textarea data-testid="brand-hero-text" value={d.heroText} maxLength={240} onChange={(e) => set({ heroText: e.target.value })} className="h-20 t-chat" /></Field>
              <Field label="Testo a piè di pagina"><Input data-testid="brand-footer" value={d.footerText} maxLength={160} onChange={(e) => set({ footerText: e.target.value })} /></Field>
              <Field label="Copyright footer"><Input data-testid="brand-copyright" value={d.copyright || ''} maxLength={120} placeholder="© 2026 Salvatore Raiola" onChange={(e) => set({ copyright: e.target.value })} /></Field>
              <Field label="Icona logo (se non carichi un'immagine)">
                <div className="flex flex-wrap gap-2">
                  {['', ...Object.keys(LOGO_ICONS)].map((k) => { const I = LOGO_ICONS[k]; return (
                    <button key={k || 'hex'} data-testid={`brand-icon-${k || 'default'}`} onClick={() => set({ logoIcon: k })} className={`h-10 w-10 rounded-xl border flex items-center justify-center transition-colors ${(d.logoIcon || '') === k ? 'border-cyan-400 bg-cyan-500/10' : 'border-white/10 hover:border-white/30'}`}>
                      {I ? <I className="h-5 w-5 text-violet-300" /> : <span className="text-[9px] text-slate-400">base</span>}
                    </button>
                  ); })}
                </div>
              </Field>
            </Section>
            <Section icon={Palette} title="Colori">
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button key={p.name} data-testid={`brand-preset-${p.name.replace(/\W+/g, '-').toLowerCase()}`} onClick={() => { const { name, ...colors } = p; set(colors); }} className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-[11px] text-slate-300 hover:border-white/30">
                    <span className="flex -space-x-1">{[p.primary, p.accent, p.background].map((c) => <span key={c} className="h-3.5 w-3.5 rounded-full border border-white/20" style={{ background: c }} />)}</span>{p.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorField k="primary" label="Colore principale" value={d.primary} onChange={(v) => set({ primary: v })} />
                <ColorField k="accent" label="Colore accento" value={d.accent} onChange={(v) => set({ accent: v })} />
                <ColorField k="background" label="Sfondo" value={d.background} onChange={(v) => set({ background: v })} />
                <ColorField k="surface" label="Sfondo schede" value={d.surface} onChange={(v) => set({ surface: v })} />
                <ColorField k="border" label="Bordi" value={d.border || '#2a2350'} onChange={(v) => set({ border: v })} />
              </div>
            </Section>
            <Section icon={Type} title="Caratteri delle lettere">
              <div className="grid sm:grid-cols-3 gap-4">
                <FontField k="display" label="Titoli e logo" value={d.fontDisplay} onChange={(v) => set({ fontDisplay: v })} />
                <FontField k="ui" label="Interfaccia" value={d.fontUi} onChange={(v) => set({ fontUi: v })} />
                <FontField k="text" label="Testi e chat" value={d.fontText} onChange={(v) => set({ fontText: v })} />
              </div>
            </Section>
          </div>
          <aside className="lg:sticky lg:top-6 h-fit space-y-3">
            <div className="t-label">Anteprima</div>
            <div data-testid="brand-preview" className="rounded-3xl overflow-hidden border border-violet-500/30 bg-titan-bg">
              <div className="h-12 px-4 flex items-center gap-2 border-b border-violet-500/20 bg-titan-surface">
                {(() => { const I = LOGO_ICONS[d.logoIcon]; return logoUrl ? <img src={logoUrl} alt="" className="h-5 w-5 rounded object-contain" /> : I ? <I className="h-5 w-5 text-violet-400" /> : <span className="h-3 w-3 rotate-45 bg-violet-500" />; })()}<span className="text-xs font-extrabold tracking-[.25em] neon-text">{d.name.toUpperCase()}</span>
              </div>
              <div className="p-5 space-y-3">
                <div className="t-label text-cyan-300">{`${d.name} ${d.heroLabel}`.toUpperCase()}</div>
                <h1 className="text-3xl font-extrabold text-white leading-tight">{d.heroTitle}</h1>
                <p className="t-chat text-xs text-slate-300">{d.heroText}</p>
                <div className="flex gap-2 pt-1">
                  <span className="rounded-full px-4 py-2 text-xs font-bold bg-gradient-to-r from-violet-600 to-cyan-600 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" />GIOCA ORA</span>
                  <span className="rounded-full px-4 py-2 text-xs border border-cyan-400/50 text-cyan-200">Installa</span>
                </div>
                <div className="glass rounded-xl p-3 text-xs text-slate-300 t-chat">Pannello di esempio con testo normale.</div>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 t-chat">L'anteprima si applica subito a tutta l'app; i giocatori vedono le modifiche dopo "Salva e pubblica". Il nome dell'icona già installata sulla Home si aggiorna solo reinstallando.</p>
          </aside>
        </div>
      </div>
    </div>
  );
}
